import React from 'react';
import { PersonAvatar } from './PersonAvatar';
import type { CachedEmail } from '../../stores/email-store';

/**
 * Inline email card rendered inside a PA message. Clicking opens the email
 * in the Focus pane (Phase R3) - for R2 it just selects the email in
 * appStore, which the legacy CatchupPanel picks up.
 */

interface EmailInlineCardProps {
  email: CachedEmail;
  onOpen: () => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    now.getFullYear() === d.getFullYear() &&
    now.getMonth() === d.getMonth() &&
    now.getDate() === d.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const diffDays = Math.floor(
    (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function EmailInlineCard({ email, onOpen }: EmailInlineCardProps) {
  const urgent = isUrgent(email);
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        transition: 'border-color 150ms ease, transform 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--accent)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 6,
        }}
      >
        <PersonAvatar
          name={email.fromName || email.fromEmail}
          email={email.fromEmail}
          size={22}
        />
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
          {email.fromName || email.fromEmail}
        </span>
        {urgent && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9.5,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              padding: '1px 6px',
              background: 'var(--accent-wash)',
              borderRadius: 3,
            }}
          >
            urgent
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--text-tertiary)',
          }}
        >
          {formatTime(email.receivedAt)}
        </span>
      </div>
      <div
        style={{
          fontSize: 14.5,
          color: 'var(--text-primary)',
          marginBottom: 4,
          fontWeight: 500,
          letterSpacing: '-0.1px',
        }}
      >
        {email.subject}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {email.snippet}
      </div>
    </button>
  );
}

function isUrgent(email: CachedEmail): boolean {
  if (email.isStarred) return true;
  const text = `${email.subject} ${email.snippet}`.toLowerCase();
  return /\b(urgent|asap|eod|eow|tomorrow|today|by\s+end|deadline)\b/.test(text);
}
